import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, Package } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { useBranchStore } from '../../stores/branch.store';
import {
  useInventory,
  useLowStockAlerts,
  useExpiryAlerts,
} from '../../api/queries/inventory.queries';
import InventoryTable from './components/InventoryTable';
import AdjustModal from './components/AdjustModal';

function InventoryPage() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // Use selected branch, fallback to user's branch
  const branchId = selectedBranchId || user?.branchId;

  const { data: inventoryItems, isLoading } = useInventory(branchId);
  const { data: lowStockAlerts } = useLowStockAlerts(branchId);
  const { data: expiryAlerts } = useExpiryAlerts(branchId, 7); // 7 days

  const handleAdjust = (item: any) => {
    setSelectedItem(item);
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <h1 className="text-2xl font-bold">{t('inventory.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {inventoryItems?.length || 0} {t('inventory.totalItems') || 'total items'}
        </p>
      </div>

      {/* Alerts Section */}
      {(lowStockAlerts && lowStockAlerts.length > 0) || (expiryAlerts && expiryAlerts.length > 0) ? (
        <div className="border-b bg-card p-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Low Stock Alerts */}
            {lowStockAlerts && lowStockAlerts.length > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <h3 className="font-medium text-orange-900 dark:text-orange-100">
                    {t('inventory.lowStock')} ({lowStockAlerts.length})
                  </h3>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {lowStockAlerts.slice(0, 5).map((alert, idx) => (
                    <div key={idx} className="text-sm text-orange-800 dark:text-orange-200">
                      <span className="font-medium">{alert.ingredientName}</span>:{' '}
                      {alert.currentStock.toFixed(2)} {alert.unit} (Reorder: {alert.reorderLevel.toFixed(2)})
                    </div>
                  ))}
                  {lowStockAlerts.length > 5 && (
                    <div className="text-xs text-orange-600">
                      +{lowStockAlerts.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expiry Alerts */}
            {expiryAlerts && expiryAlerts.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-red-600" />
                  <h3 className="font-medium text-red-900 dark:text-red-100">
                    {t('inventory.expiringSoon')} ({expiryAlerts.length})
                  </h3>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {expiryAlerts.slice(0, 5).map((alert, idx) => (
                    <div key={idx} className="text-sm text-red-800 dark:text-red-200">
                      <span className="font-medium">{alert.ingredientName}</span> (Batch: {alert.batchNumber}):{' '}
                      {alert.daysUntilExpiry} {t('inventory.daysLeft') || 'days left'}
                    </div>
                  ))}
                  {expiryAlerts.length > 5 && (
                    <div className="text-xs text-red-600">
                      +{expiryAlerts.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Package className="h-12 w-12 animate-pulse text-muted-foreground" />
          </div>
        ) : !branchId ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Package className="h-16 w-16 mb-4 opacity-20" />
            <p className="text-lg font-medium">Please select a branch</p>
            <p className="text-sm">Use the branch selector in the sidebar to view inventory</p>
          </div>
        ) : inventoryItems && inventoryItems.length > 0 ? (
          <InventoryTable items={inventoryItems} onAdjust={handleAdjust} />
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Package className="h-16 w-16 mb-4 opacity-20" />
            <p>{t('inventory.noItems') || 'No inventory items'}</p>
          </div>
        )}
      </div>

      {/* Adjust Modal */}
      {selectedItem && (
        <AdjustModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}

export default InventoryPage;
